import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { AppSidebar } from '../components/AppSidebar'
import { SessionWarning } from '../components/SessionWarning'
import { SidebarProvider, useSidebar } from '../contexts/SidebarContext'
import { useSessionChecker } from '../hooks/useSessionChecker'

function SessionWarningBanner() {
  const { warning, extendSession, dismissWarning } = useSessionChecker()

  if (!warning.show) return null

  return (
    <div className="bg-orange-50 border-b border-orange-200 px-6 py-2">
      <SessionWarning
        message={warning.message}
        timeRemaining={warning.timeRemaining}
        onExtend={extendSession}
        onDismiss={dismissWarning}
      />
    </div>
  )
}

function MainLayoutContent({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const isReviewPage = location.pathname.startsWith('/review/')
  const { setIsCollapsed, isCollapsed } = useSidebar()
  const prevIsReviewPageRef = useRef(false)
  const savedStateBeforeReviewRef = useRef<boolean | null>(null)

  useEffect(() => {
    if (isReviewPage && !prevIsReviewPageRef.current) {
      savedStateBeforeReviewRef.current = isCollapsed
      setIsCollapsed(true)
    } else if (!isReviewPage && prevIsReviewPageRef.current) {
      if (savedStateBeforeReviewRef.current !== null) {
        setIsCollapsed(savedStateBeforeReviewRef.current)
        savedStateBeforeReviewRef.current = null
      }
    }
    prevIsReviewPageRef.current = isReviewPage
  }, [isReviewPage, setIsCollapsed, isCollapsed])

  return (
    <div className="min-h-screen bg-main flex">
      <AppSidebar />
      <div className="flex-1 flex flex-col min-h-screen">
        <SessionWarningBanner />
        <main className="flex-1 px-6 py-6">
          {children}
        </main>
      </div>
    </div>
  )
}

export function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <MainLayoutContent>{children}</MainLayoutContent>
    </SidebarProvider>
  )
}
